import React, { useCallback, useEffect, useState } from "react";
import { PageHeader } from "../components/layout/Shell";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { useInstitution } from "../context/InstitutionContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Network,
  Plus,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Link2,
  Unlink,
  Loader2,
  Info,
  Edit,
  BookOpen,
  Settings,
  Building2,
  GitBranch,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem("academiaos_token");
}

// ── Status Badge ───────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    DRAFT:    { label: "Draft",   className: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700" },
    ACTIVE:   { label: "Active",  className: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700" },
    RETIRED:  { label: "Retired", className: "bg-slate-100 dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-600" },
  };
  const s = map[status] || map.DRAFT;
  return <Badge variant="outline" className={`text-xs ${s.className}`}>{s.label}</Badge>;
}

// ── Level Badge ────────────────────────────────────────────────────────────────
function LevelBadge({ level }) {
  const map = {
    1: { label: "Topic",    className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200" },
    2: { label: "Sub-topic", className: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200" },
    3: { label: "Leaf",     className: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200" },
  };
  const m = map[level] || map[1];
  return <Badge variant="outline" className={`text-xs ${m.className}`}>{m.label}</Badge>;
}

// ── New Concept Dialog ─────────────────────────────────────────────────────────
function NewConceptDialog({ open, onClose, institutionId, concepts, onSuccess }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [level, setLevel] = useState("3");
  const [programmeCode, setProgrammeCode] = useState("CSE");
  const [parentId, setParentId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setCode(""); setName(""); setDesc(""); setLevel("3"); setProgrammeCode("CSE"); setParentId(""); };

  const handleSubmit = async () => {
    if (!code.trim() || !name.trim()) {
      toast.error("Code and name are required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(
        `${API}/api/alm/concepts?institution_id=${institutionId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({
            code: code.trim().toUpperCase(),
            name: name.trim(),
            description: desc.trim() || null,
            level: parseInt(level),
            programme_code: (programmeCode && programmeCode !== "CROSS") ? programmeCode : null,
            parent_concept_id: (parentId && parentId !== "__none__") ? parentId : null,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        toast.error("Create failed", { description: data.detail || "Unknown error" });
        return;
      }
      toast.success(`Concept ${data.concept.code} created (DRAFT)`);
      onSuccess(data.concept);
      reset();
      onClose();
    } catch {
      toast.error("Network error creating concept.");
    } finally {
      setSubmitting(false);
    }
  };

  const topicConcepts = concepts.filter(c => c.level < parseInt(level) && c.status !== "RETIRED");

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-md" data-testid="alm-new-concept-dialog">
        <DialogHeader>
          <DialogTitle>New Concept</DialogTitle>
          <DialogDescription>
            Create a taxonomy concept node. Status starts as DRAFT; activate when ready.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Code *</label>
              <Input data-testid="alm-concept-code-input" placeholder="CSE-DS-C01" value={code}
                     onChange={e => setCode(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Programme</label>
              <Select value={programmeCode} onValueChange={setProgrammeCode}>
                <SelectTrigger className="mt-1" data-testid="alm-programme-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CSE">CSE</SelectItem>
                  <SelectItem value="ECE">ECE</SelectItem>
                  <SelectItem value="CROSS">Cross-programme</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Name *</label>
            <Input data-testid="alm-concept-name-input" placeholder="Arrays and Memory Layout"
                   value={name} onChange={e => setName(e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Description</label>
            <Input placeholder="Optional description" value={desc}
                   onChange={e => setDesc(e.target.value)} className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Level</label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger className="mt-1" data-testid="alm-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 — Topic</SelectItem>
                  <SelectItem value="2">2 — Sub-topic</SelectItem>
                  <SelectItem value="3">3 — Leaf (assessable)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Parent concept</label>
              <Select value={parentId} onValueChange={setParentId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="None (root)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (root)</SelectItem>
                  {topicConcepts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/30 rounded-md px-3 py-2">
            <Info className="inline h-3 w-3 mr-1 text-blue-500" />
            Only Level 3 (leaf) concepts are directly assessable. Topics group leaves for navigation.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          <Button data-testid="alm-create-concept-btn" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Create Concept
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Prereq Dialog ──────────────────────────────────────────────────────────────
function PrereqDialog({ open, onClose, concept, institutionId, allConcepts, existingPrereqs, onSuccess }) {
  const [prereqId, setPrereqId] = useState("");
  const [strength, setStrength] = useState("HARD");
  const [submitting, setSubmitting] = useState(false);

  const existingPrereqIds = new Set(existingPrereqs.map(e => e.prereq_concept_id));
  const candidates = allConcepts.filter(c =>
    c.id !== concept?.id &&
    !existingPrereqIds.has(c.id) &&
    c.status !== "RETIRED"
  );

  const handleAdd = async () => {
    if (!prereqId) { toast.error("Select a prerequisite concept."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(
        `${API}/api/alm/concepts/${concept.id}/prereqs?institution_id=${institutionId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ prereq_concept_id: prereqId, strength }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 409) {
          toast.error("Prerequisite blocked", {
            description: data.detail?.includes("cycle")
              ? "Adding this prerequisite would create a cycle in the concept DAG."
              : data.detail || "This edge already exists.",
          });
        } else {
          toast.error("Failed to add prerequisite", { description: data.detail });
        }
        return;
      }
      toast.success("Prerequisite added");
      onSuccess(data.edge);
      setPrereqId("");
    } catch {
      toast.error("Network error.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (prereqConceptId) => {
    try {
      const res = await fetch(
        `${API}/api/alm/concepts/${concept.id}/prereqs/${prereqConceptId}?institution_id=${institutionId}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (!res.ok) {
        const data = await res.json();
        toast.error("Remove failed", { description: data.detail });
        return;
      }
      toast.success("Prerequisite removed");
      onSuccess(null, prereqConceptId);
    } catch {
      toast.error("Network error.");
    }
  };

  const getConceptByIdMap = Object.fromEntries(allConcepts.map(c => [c.id, c]));

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="alm-prereq-dialog">
        <DialogHeader>
          <DialogTitle>Prerequisites — {concept?.code}</DialogTitle>
          <DialogDescription>
            Manage prerequisite edges. Adding an edge that creates a cycle is blocked (INV-ALM-01-02).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Existing prereqs */}
          {existingPrereqs.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Existing prerequisites</p>
              {existingPrereqs.map(e => {
                const pc = getConceptByIdMap[e.prereq_concept_id];
                return (
                  <div key={e.id} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm"
                       data-testid={`prereq-item-${e.prereq_concept_id}`}>
                    <div className="flex items-center gap-2">
                      <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-medium">{pc?.code || e.prereq_concept_id.slice(0, 8)}</span>
                      <span className="text-muted-foreground">{pc?.name}</span>
                      <Badge variant="outline" className={`text-xs ${e.strength === 'HARD'
                        ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                        {e.strength}
                      </Badge>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                            onClick={() => handleRemove(e.prereq_concept_id)}
                            data-testid={`prereq-remove-${e.prereq_concept_id}`}>
                      <Unlink className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-2">No prerequisites defined yet.</p>
          )}
          {/* Add new prereq */}
          <div className="border-t pt-3 space-y-3">
            <p className="text-xs font-medium text-muted-foreground">Add prerequisite</p>
            <div className="flex gap-2">
              <Select value={prereqId} onValueChange={setPrereqId}>
                <SelectTrigger className="flex-1" data-testid="alm-prereq-select">
                  <SelectValue placeholder="Select concept..." />
                </SelectTrigger>
                <SelectContent>
                  {candidates.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.code} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={strength} onValueChange={setStrength}>
                <SelectTrigger className="w-28" data-testid="alm-strength-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HARD">HARD</SelectItem>
                  <SelectItem value="SOFT">SOFT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="alm-add-prereq-btn" onClick={handleAdd}
                    disabled={submitting || !prereqId} className="w-full">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
              Add Prerequisite
            </Button>
          </div>
          <p className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/30 rounded-md px-3 py-2">
            <Info className="inline h-3 w-3 mr-1 text-blue-500" />
            HARD = blocking (concept cannot be considered mastered without prerequisite).
            SOFT = recommended only.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Mastery Config Panel ───────────────────────────────────────────────────────
function MasteryConfigPanel({ institutionId, canManage }) {
  const [config, setConfig] = useState(null);
  const [editing, setEditing] = useState(false);
  const [mastered, setMastered] = useState("80");
  const [partial, setPartial] = useState("50");
  const [minItems, setMinItems] = useState("2");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/alm/mastery-config?institution_id=${institutionId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setMastered(String(data.mastered_min_score));
        setPartial(String(data.partial_min_score));
        setMinItems(String(data.min_evidence_items));
        setNotes(data.scoring_rule_notes || "");
      }
    } catch {}
  }, [institutionId]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSave = async () => {
    if (parseFloat(mastered) <= parseFloat(partial)) {
      toast.error("MASTERED threshold must be greater than PARTIAL threshold.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/alm/mastery-config?institution_id=${institutionId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({
            mastered_min_score: parseFloat(mastered),
            partial_min_score: parseFloat(partial),
            min_evidence_items: parseInt(minItems),
            scoring_rule_notes: notes || null,
          }),
        });
      const data = await res.json();
      if (!res.ok) { toast.error("Save failed", { description: data.detail }); return; }
      setConfig(data.config);
      setEditing(false);
      toast.success("Mastery configuration updated (new version created).");
    } catch {
      toast.error("Network error.");
    } finally {
      setSaving(false);
    }
  };

  if (!config) return null;

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3" data-testid="alm-mastery-config-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Mastery Configuration</h3>
          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
            Provisional Pilot Rules
          </Badge>
        </div>
        {canManage && !editing && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid="alm-edit-config-btn">
            <Edit className="h-3.5 w-3.5 mr-1" /> Edit
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 p-2 text-center">
          <p className="text-xs text-muted-foreground">MASTERED</p>
          <p className="font-semibold text-emerald-700 dark:text-emerald-300">≥ {config.mastered_min_score}%</p>
        </div>
        <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 p-2 text-center">
          <p className="text-xs text-muted-foreground">PARTIAL</p>
          <p className="font-semibold text-amber-700 dark:text-amber-300">≥ {config.partial_min_score}%</p>
        </div>
        <div className="rounded-md bg-red-50 dark:bg-red-950/30 p-2 text-center">
          <p className="text-xs text-muted-foreground">NOT_MASTERED</p>
          <p className="font-semibold text-red-700 dark:text-red-300">&lt; {config.partial_min_score}%</p>
        </div>
        <div className="rounded-md bg-slate-50 dark:bg-slate-800 p-2 text-center">
          <p className="text-xs text-muted-foreground">NO_EVIDENCE</p>
          <p className="font-semibold text-slate-600 dark:text-slate-300">&lt; {config.min_evidence_items} items</p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        <Info className="inline h-3 w-3 mr-1" />
        These thresholds are provisional pilot configuration. Not certified academic policy (INV-ALM-01-07).
        Engine v{config.engine_version}.
      </p>
      {editing && (
        <div className="border-t pt-3 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">MASTERED ≥ (%)</label>
              <Input type="number" min="1" max="100" value={mastered} onChange={e => setMastered(e.target.value)}
                     className="mt-1" data-testid="alm-mastered-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">PARTIAL ≥ (%)</label>
              <Input type="number" min="0" max="99" value={partial} onChange={e => setPartial(e.target.value)}
                     className="mt-1" data-testid="alm-partial-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Min evidence items</label>
              <Input type="number" min="1" value={minItems} onChange={e => setMinItems(e.target.value)}
                     className="mt-1" data-testid="alm-min-items-input" />
            </div>
          </div>
          <Input placeholder="Notes on this threshold update..." value={notes} onChange={e => setNotes(e.target.value)} />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="alm-save-config-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              Save New Version
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function ALMConceptManager() {
  const { user } = useAuth();
  const { current, institutions, switchInstitution } = useInstitution();
  const isSuperAdmin = user?.role === "super_admin";
  const canManage = ["institution_admin", "super_admin", "dean", "hod"].includes(user?.role);

  const [concepts, setConcepts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dagStatus, setDagStatus] = useState(null); // { is_valid, edge_count }
  const [filterProg, setFilterProg] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [prereqTarget, setPrereqTarget] = useState(null);
  const [prereqEdges, setPrereqEdges] = useState([]);

  const institutionId = current?.id;

  const load = useCallback(async () => {
    if (!institutionId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ institution_id: institutionId, limit: "200", include_synthetic: "true" });
      if (filterProg !== "ALL") params.set("programme_code", filterProg);
      if (filterStatus !== "ALL") params.set("status", filterStatus);
      const res = await fetch(`${API}/api/alm/concepts?${params}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      if (res.ok) {
        const data = await res.json();
        setConcepts(data.items || []);
        setTotal(data.total || 0);
      }
    } catch {}
    setLoading(false);
  }, [institutionId, filterProg, filterStatus]);

  const loadDagStatus = useCallback(async () => {
    if (!institutionId) return;
    try {
      const res = await fetch(
        `${API}/api/alm/dag/validate?institution_id=${institutionId}`,
        { method: "POST", headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (res.ok) { const d = await res.json(); setDagStatus(d); }
    } catch {}
  }, [institutionId]);

  useEffect(() => { load(); loadDagStatus(); }, [load, loadDagStatus]);

  const loadPrereqs = async (conceptId) => {
    try {
      const res = await fetch(
        `${API}/api/alm/concepts/${conceptId}/prereqs?institution_id=${institutionId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (res.ok) { const d = await res.json(); setPrereqEdges(d.items || []); }
    } catch {}
  };

  const handleActivate = async (conceptId) => {
    try {
      const res = await fetch(
        `${API}/api/alm/concepts/${conceptId}/activate?institution_id=${institutionId}`,
        { method: "POST", headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const data = await res.json();
      if (!res.ok) { toast.error("Activate failed", { description: data.detail }); return; }
      toast.success("Concept activated");
      load(); loadDagStatus();
    } catch { toast.error("Network error."); }
  };

  const handleRetire = async (conceptId) => {
    try {
      const res = await fetch(
        `${API}/api/alm/concepts/${conceptId}/retire?institution_id=${institutionId}`,
        { method: "POST", headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const data = await res.json();
      if (!res.ok) { toast.error("Retire failed", { description: data.detail }); return; }
      toast.success("Concept retired");
      load(); loadDagStatus();
    } catch { toast.error("Network error."); }
  };

  const filteredConcepts = concepts.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q);
  });

  if (!current) return null;

  return (
    <div data-testid="alm-concept-manager-page">
      {/* Institution selector for super_admin */}
      {isSuperAdmin && institutions.length > 1 && (
        <div className="px-6 lg:px-8 pt-4 pb-0">
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 max-w-xs">
            <Building2 className="h-3.5 w-3.5 text-amber-600 shrink-0" />
            <span className="text-xs text-amber-700 shrink-0">Institution:</span>
            <Select value={current.id} onValueChange={switchInstitution}>
              <SelectTrigger className="h-7 text-xs border-0 bg-transparent p-0 focus:ring-0 shadow-none" data-testid="alm-institution-switcher">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {institutions.map(inst => (
                  <SelectItem key={inst.id} value={inst.id} className="text-xs">
                    {inst.name || inst.short_name || inst.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <PageHeader
        title="Concept Taxonomy"
        subtitle="Manage the adaptive learning concept vocabulary and prerequisite graph"
        actions={
          <div className="flex items-center gap-2">
            {/* DAG status badge */}
            {dagStatus && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <div className={`flex items-center gap-1 rounded-full px-2 py-1 text-xs border ${
                      dagStatus.is_valid
                        ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border-emerald-200"
                        : "bg-red-50 dark:bg-red-950/30 text-red-700 border-red-200"
                    }`} data-testid="alm-dag-status">
                      {dagStatus.is_valid
                        ? <CheckCircle2 className="h-3 w-3" />
                        : <AlertTriangle className="h-3 w-3" />}
                      DAG {dagStatus.is_valid ? "valid" : "CYCLIC"}
                      <span className="opacity-60 ml-1">{dagStatus.edge_count}e</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {dagStatus.is_valid
                      ? `Concept DAG is cycle-free. ${dagStatus.concept_count} concepts, ${dagStatus.edge_count} prerequisite edges.`
                      : "DAG contains a cycle! No new concepts can be recommended until resolved."}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <Button variant="outline" size="sm" onClick={() => { load(); loadDagStatus(); }} data-testid="alm-refresh-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
            {canManage && (
              <Button size="sm" onClick={() => setShowNew(true)} data-testid="alm-new-concept-btn">
                <Plus className="h-4 w-4 mr-1" /> New Concept
              </Button>
            )}
          </div>
        }
      />

      <div className="px-6 lg:px-8 pb-8 space-y-4">
        {/* Mastery config panel */}
        <MasteryConfigPanel institutionId={institutionId} canManage={canManage} />

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search code or name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-8 w-48 text-sm"
            data-testid="alm-search-input"
          />
          <div className="flex gap-1">
            {["ALL", "CSE", "ECE"].map(p => (
              <Button
                key={p}
                variant={filterProg === p ? "default" : "outline"}
                size="sm"
                className="h-8 text-xs"
                onClick={() => setFilterProg(p)}
                data-testid={`alm-filter-prog-${p.toLowerCase()}`}
              >
                {p}
              </Button>
            ))}
          </div>
          <div className="flex gap-1 ml-2">
            {["ALL", "DRAFT", "ACTIVE", "RETIRED"].map(s => (
              <Button
                key={s}
                variant={filterStatus === s ? "default" : "outline"}
                size="sm"
                className="h-8 text-xs"
                onClick={() => setFilterStatus(s)}
                data-testid={`alm-filter-status-${s.toLowerCase()}`}
              >
                {s === "ALL" ? `All (${total})` : s}
              </Button>
            ))}
          </div>
        </div>

        {/* Concept table */}
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium text-xs text-muted-foreground">Code</th>
                  <th className="text-left px-4 py-2.5 font-medium text-xs text-muted-foreground">Name</th>
                  <th className="text-left px-4 py-2.5 font-medium text-xs text-muted-foreground hidden md:table-cell">Programme</th>
                  <th className="text-left px-4 py-2.5 font-medium text-xs text-muted-foreground hidden sm:table-cell">Level</th>
                  <th className="text-left px-4 py-2.5 font-medium text-xs text-muted-foreground">Status</th>
                  <th className="text-right px-4 py-2.5 font-medium text-xs text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Loading...
                  </td></tr>
                ) : filteredConcepts.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">
                    <GitBranch className="h-8 w-8 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No concepts yet.</p>
                    {canManage && <p className="text-xs mt-1">Click "New Concept" to build the taxonomy.</p>}
                  </td></tr>
                ) : filteredConcepts.map(c => (
                  <tr key={c.id} className="hover:bg-muted/20 transition-colors"
                      data-testid={`alm-concept-row-${c.code}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs font-semibold">{c.code}</span>
                        {c.is_synthetic && (
                          <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-600 border-purple-200">
                            SYNTH
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium">{c.name}</p>
                      {c.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{c.description}</p>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-xs text-muted-foreground">{c.programme_code || "All"}</span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <LevelBadge level={c.level} />
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {canManage && (
                          <Button
                            variant="ghost" size="sm" className="h-7 text-xs"
                            onClick={async () => { setPrereqTarget(c); await loadPrereqs(c.id); }}
                            data-testid={`alm-prereq-btn-${c.code}`}
                          >
                            <Link2 className="h-3 w-3 mr-1" /> Prereqs
                          </Button>
                        )}
                        {canManage && c.status === "DRAFT" && (
                          <Button
                            variant="ghost" size="sm" className="h-7 text-xs text-emerald-600 hover:text-emerald-700"
                            onClick={() => handleActivate(c.id)}
                            data-testid={`alm-activate-btn-${c.code}`}
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Activate
                          </Button>
                        )}
                        {canManage && c.status === "ACTIVE" && (
                          <Button
                            variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-destructive"
                            onClick={() => handleRetire(c.id)}
                            data-testid={`alm-retire-btn-${c.code}`}
                          >
                            Retire
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          <Info className="inline h-3 w-3 mr-1" />
          Mastery states (MASTERED / PARTIAL / NOT_MASTERED / NO_EVIDENCE) are advisory only —
          they do not modify academic records, grades, or official standing (INV-ALM-01-07).
        </p>
      </div>

      {/* Dialogs */}
      <NewConceptDialog
        open={showNew}
        onClose={() => setShowNew(false)}
        institutionId={institutionId}
        concepts={concepts}
        onSuccess={() => { load(); loadDagStatus(); }}
      />
      {prereqTarget && (
        <PrereqDialog
          open={!!prereqTarget}
          onClose={() => { setPrereqTarget(null); setPrereqEdges([]); }}
          concept={prereqTarget}
          institutionId={institutionId}
          allConcepts={concepts}
          existingPrereqs={prereqEdges}
          onSuccess={(edge, removedId) => {
            if (removedId) {
              setPrereqEdges(prev => prev.filter(e => e.prereq_concept_id !== removedId));
            } else if (edge) {
              setPrereqEdges(prev => [...prev, edge]);
            }
            loadDagStatus();
          }}
        />
      )}
    </div>
  );
}
